import React, { useState, useEffect } from 'react';
import {
  FaBuilding, FaDoorOpen, FaUserTie, FaUsers, FaMapMarkerAlt,
  FaEnvelope, FaPhone, FaParking, FaWifi, FaUtensils, FaDesktop,
  FaPlus, FaEdit, FaTrash, FaTimes, FaSearch, FaStar, FaGlobeAmericas
} from 'react-icons/fa';
import useILTResourceManagementStore from '@/stores/AdminStore/ILTResourceManagementStore';
import './ILTResourseManagement.css';

const ILTManagement = () => {
  const {
    locations: storeLocations,
    rooms: storeRooms,
    trainers: storeTrainers,
    stats: storeStats,
    fetchLocations,
    fetchRooms,
    fetchTrainers,
    fetchStats,
  } = useILTResourceManagementStore();

  const [activeTab, setActiveTab] = useState('locations');
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Lazy load data based on active tab
  useEffect(() => {
    // Always fetch stats on mount
    if (!storeStats) {
      fetchStats();
    }
    if (activeTab === 'locations' && !storeLocations) {
      fetchLocations();
    }
    if (activeTab === 'rooms' && !storeRooms) {
      fetchRooms();
    }
    if (activeTab === 'trainers' && !storeTrainers) {
      fetchTrainers();
    }
  }, [activeTab, storeStats, storeLocations, storeRooms, storeTrainers, fetchStats, fetchLocations, fetchRooms, fetchTrainers]);

  const [locations, setLocations] = useState(storeLocations || []);

  // Sync store data to local state
  useEffect(() => {
    if (storeLocations) {
      setLocations(storeLocations);
    }
  }, [storeLocations]);

  const [rooms, setRooms] = useState(storeRooms || []);

  useEffect(() => {
    if (storeRooms) {
      setRooms(storeRooms);
    }
  }, [storeRooms]);

  const [roomFormData, setRoomFormData] = useState({
    name: '',
    roomNumber: '',
    floor: '',
    location: '',
    type: 'Conference',
    capacity: '',
    area: '',
    description: '',
    amenities: [] as string[]
  });

  const [trainers, setTrainers] = useState(storeTrainers || []);

  useEffect(() => {
    if (storeTrainers) {
      setTrainers(storeTrainers);
    }
  }, [storeTrainers]);

  const [trainerFormData, setTrainerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    expertise: [] as string[],
    certifications: [] as string[],
    experience: '',
    rate: '',
    availability: 'Full-Time',
    languages: [] as string[],
    isExternal: false
  });

  const [formData, setFormData] = useState({
    name: '', address: '', city: '', state: '', zipCode: '',
    country: 'USA', timezone: '', capacity: '', phone: '', email: '',
    facilities: [] as string[]
  });

  const stats = storeStats || {
    totalLocations: 0,
    availableRooms: 0,
    activeTrainers: 0,
    totalCapacity: 0
  };

  const facilityOptions = [
    { id: 'parking', label: 'Parking', icon: <FaParking /> },
    { id: 'cafeteria', label: 'Cafeteria', icon: <FaUtensils /> },
    { id: 'wifi', label: 'WiFi', icon: <FaWifi /> },
    { id: 'av', label: 'AV Equipment', icon: <FaDesktop /> },
    { id: 'reception', label: 'Reception' },
    { id: 'catering', label: 'Catering' },
    { id: 'security', label: 'Security' },
    { id: 'accessible', label: 'Accessible' }
  ];

  const amenityOptions = [
    'Projector',
    'Whiteboard',
    'WiFi',
    'Computers',
    'Video Conferencing',
    'Air Conditioning',
    'Catering Available',
    'Parking',
    'Streaming Equipment'
  ];

  const expertiseOptions = [
    'Leadership',
    'Project Management',
    'Agile',
    'Data Science',
    'Sales Training',
    'Communication',
    'Technical Skills',
    'Soft Skills',
    'Change Management',
    'Customer Success',
    'Strategy'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoomInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setRoomFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFacilityToggle = (facility: string) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...prev.facilities, facility]
    }));
  };

  const handleAmenityToggle = (amenity: string) => {
    setRoomFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleTrainerInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setTrainerFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setTrainerFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleExpertiseToggle = (expertise: string) => {
    setTrainerFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(expertise)
        ? prev.expertise.filter(e => e !== expertise)
        : [...prev.expertise, expertise]
    }));
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.address || !formData.city || !formData.state) {
      alert('Please fill in all required fields');
      return;
    }

    const newLocation = {
      id: locations.length + 1,
      name: formData.name,
      city: `${formData.city}, ${formData.state}`,
      address: formData.address,
      email: formData.email,
      phone: formData.phone,
      capacity: parseInt(formData.capacity) || 0,
      rooms: Math.floor((parseInt(formData.capacity) || 0) / 30),
      timezone: formData.timezone,
      manager: 'Unassigned',
      facilities: formData.facilities,
      status: 'Active'
    };
    setLocations([...locations, newLocation]);
    setShowModal(false);
    setFormData({
      name: '', address: '', city: '', state: '', zipCode: '',
      country: 'USA', timezone: '', capacity: '', phone: '', email: '',
      facilities: [] as string[]
    });
  };

  const handleRoomSubmit = () => {
    if (!roomFormData.name || !roomFormData.location || !roomFormData.capacity) {
      alert('Please fill in all required fields');
      return;
    }

    const newRoom = {
      id: rooms.length + 1,
      name: roomFormData.name,
      roomNumber: roomFormData.roomNumber,
      location: roomFormData.location,
      locationId: 0, // TODO: Link to actual location when integrated with backend
      floor: roomFormData.floor,
      type: roomFormData.type,
      capacity: parseInt(roomFormData.capacity) || 0,
      area: roomFormData.area,
      description: roomFormData.description,
      amenities: roomFormData.amenities,
      status: 'Active'
    };
    setRooms([...rooms, newRoom]);
    setShowModal(false);
    setRoomFormData({
      name: '',
      roomNumber: '',
      floor: '',
      location: '',
      type: 'Conference',
      capacity: '',
      area: '',
      description: '',
      amenities: [] as string[]
    });
  };

  const handleTrainerSubmit = () => {
    if (!trainerFormData.firstName || !trainerFormData.lastName || !trainerFormData.email) {
      alert('Please fill in all required fields');
      return;
    }

    const initials = `${trainerFormData.firstName.charAt(0)}${trainerFormData.lastName.charAt(0)}`;
    const colors = ['#9C27B0', '#E91E63', '#2196F3', '#FF9800', '#4CAF50'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    const newTrainer = {
      id: trainers.length + 1,
      userId: '', // TODO: Link to actual user when integrated with backend
      firstName: trainerFormData.firstName,
      lastName: trainerFormData.lastName,
      email: trainerFormData.email,
      phone: trainerFormData.phone,
      bio: trainerFormData.bio,
      rating: 0,
      sessions: 0,
      expertise: trainerFormData.expertise,
      certifications: trainerFormData.certifications,
      experience: parseInt(trainerFormData.experience) || 0,
      rate: parseInt(trainerFormData.rate) || 0,
      availability: trainerFormData.availability,
      languages: trainerFormData.languages,
      status: 'Active',
      initials: initials.toUpperCase(),
      color: randomColor
    };
    setTrainers([...trainers, newTrainer]);
    setShowModal(false);
    setTrainerFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      bio: '',
      expertise: [] as string[],
      certifications: [] as string[],
      experience: '',
      rate: '',
      availability: 'Full-Time',
      languages: [] as string[],
      isExternal: false
    });
  };

  const filteredLocations = locations.filter(loc => {
    const matchesStatus = filterStatus === 'active'
      ? loc.status.toLowerCase() === 'active'
      : true;
    const matchesSearch = loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         loc.city.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredRooms = rooms.filter(room => {
    const matchesStatus = filterStatus === 'active'
      ? room.status.toLowerCase() === 'active'
      : true;
    const matchesSearch = room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         room.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredTrainers = trainers.filter(trainer => {
    const matchesStatus = filterStatus === 'active'
      ? trainer.status.toLowerCase() === 'active'
      : true;
    const matchesSearch = `${trainer.firstName} ${trainer.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         trainer.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getFacilityIcon = (facility: string) => {
    const lowerFacility = facility.toLowerCase();
    if (lowerFacility.includes('parking')) return <FaParking />;
    if (lowerFacility.includes('wifi')) return <FaWifi />;
    if (lowerFacility.includes('cafeteria') || lowerFacility.includes('catering')) return <FaUtensils />;
    if (lowerFacility.includes('av') || lowerFacility.includes('equipment')) return <FaDesktop />;
    return null;
  };

  const getRoomIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'conference':
        return <FaUsers />;
      case 'lab':
        return <FaDesktop />;
      case 'classroom':
        return <FaDoorOpen />;
      case 'virtual':
        return <FaDesktop />;
      default:
        return <FaDoorOpen />;
    }
  };

  const getRoomIconColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'conference':
        return '#4CAF50';
      case 'lab':
        return '#2196F3';
      case 'classroom':
        return '#FF9800';
      case 'virtual':
        return '#9C27B0';
      default:
        return '#2196F3';
    }
  };

  return (
    <div className="trainer-ilt-container">
      <div className="trainer-ilt-header">
        <div className="trainer-ilt-breadcrumb">
          <span>Tools</span>
          <span className="trainer-ilt-breadcrumb-separator">›</span>
          <span>Ilt Resources</span>
        </div>
        <h1 className="trainer-ilt-title">Tools</h1>
        <p className="trainer-ilt-subtitle">Administrative utilities and system tools</p>
      </div>

      <div className="trainer-ilt-section">
        <h2 className="trainer-ilt-section-title">ILT Resource Management</h2>
        <p className="trainer-ilt-section-subtitle">Manage training locations, rooms, and instructors</p>

        <div className="trainer-ilt-stats">
          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-blue">
              <FaBuilding />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Total Locations</div>
              <div className="trainer-ilt-stat-value">{stats.totalLocations}</div>
              <div className="trainer-ilt-stat-change">+2</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-green">
              <FaDoorOpen />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Available Rooms</div>
              <div className="trainer-ilt-stat-value">{stats.availableRooms}</div>
              <div className="trainer-ilt-stat-change">+5</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-purple">
              <FaUserTie />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Active Trainers</div>
              <div className="trainer-ilt-stat-value">{stats.activeTrainers}</div>
              <div className="trainer-ilt-stat-change">+3</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-orange">
              <FaUsers />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Total Capacity</div>
              <div className="trainer-ilt-stat-value">{stats.totalCapacity}</div>
              <div className="trainer-ilt-stat-change">+180</div>
            </div>
          </div>
        </div>

        <div className="trainer-ilt-tabs">
          <button 
            className={`trainer-ilt-tab ${activeTab === 'locations' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('locations')}
          >
            <FaBuilding /> Locations
          </button>
          <button 
            className={`trainer-ilt-tab ${activeTab === 'rooms' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('rooms')}
          >
            <FaDoorOpen /> Rooms
          </button>
          <button 
            className={`trainer-ilt-tab ${activeTab === 'trainers' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('trainers')}
          >
            <FaUserTie /> Trainers
          </button>
        </div>

        <div className="trainer-ilt-controls">
          <div className="trainer-ilt-search-container">
            <FaSearch className="trainer-ilt-search-icon" />
            <input
              type="text"
              placeholder={activeTab === 'rooms' ? 'Search Rooms' : activeTab === 'trainers' ? 'Search Trainers' : 'Search locations'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="trainer-ilt-search-input"
            />
          </div>
          <select
            className="trainer-ilt-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="active">By Status Active</option>
            <option value="all">All Status</option>
            <option value="maintenance">Maintenance</option>
          </select>
          <button
            className="trainer-ilt-btn-primary"
            onClick={() => setShowModal(true)}
          >
            <FaPlus /> {activeTab === 'rooms' ? 'Add Room' : activeTab === 'trainers' ? 'New Instructor' : 'Add Location'}
          </button>
        </div>

        {activeTab === 'locations' && (
          <div className="trainer-ilt-locations-grid">
            {filteredLocations.map(location => (
              <div key={location.id} className="trainer-ilt-location-card">
              <div className="trainer-ilt-location-header">
                <div className="trainer-ilt-location-icon">
                  <FaBuilding />
                </div>
                <div className="trainer-ilt-location-title">
                  <h3 className="trainer-ilt-location-name">{location.name}</h3>
                  <p className="trainer-ilt-location-city">{location.city}</p>
                </div>
                <span className={`trainer-ilt-status trainer-ilt-status-${location.status.toLowerCase()}`}>
                  {location.status}
                </span>
              </div>

              <div className="trainer-ilt-location-details">
                <div className="trainer-ilt-detail-item">
                  <FaMapMarkerAlt /> {location.address}
                </div>
                <div className="trainer-ilt-detail-item">
                  <FaEnvelope /> {location.email}
                </div>
                <div className="trainer-ilt-detail-item">
                  <FaPhone /> {location.phone}
                </div>
              </div>

              <div className="trainer-ilt-location-info">
                <div className="trainer-ilt-info-group">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Capacity</div>
                    <div className="trainer-ilt-info-value">{location.capacity}</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Rooms</div>
                    <div className="trainer-ilt-info-value">{location.rooms}</div>
                  </div>
                </div>
                <div className="trainer-ilt-info-group">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Timezone</div>
                    <div className="trainer-ilt-info-value">{location.timezone}</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Manager</div>
                    <div className="trainer-ilt-info-value">{location.manager}</div>
                  </div>
                </div>
              </div>

              <div className="trainer-ilt-facilities">
                <div className="trainer-ilt-facilities-label">Facilities</div>
                <div className="trainer-ilt-facilities-list">
                  {location.facilities.map((facility, idx) => (
                    <span key={idx} className="trainer-ilt-facility-tag">
                      {getFacilityIcon(facility)}
                      {facility}
                    </span>
                  ))}
                </div>
              </div>

              <div className="trainer-ilt-location-actions">
                <button className="trainer-ilt-btn-view">View Details</button>
                <button className="trainer-ilt-icon-btn">
                  <FaEdit />
                </button>
                <button className="trainer-ilt-icon-btn">
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
          </div>
        )}

        {activeTab === 'rooms' && (
          <div className="trainer-ilt-locations-grid">
            {filteredRooms.map(room => (
              <div key={room.id} className="trainer-ilt-location-card">
                <div className="trainer-ilt-location-header">
                  <div className="trainer-ilt-location-icon" style={{ backgroundColor: getRoomIconColor(room.type) }}>
                    {getRoomIcon(room.type)}
                  </div>
                  <div className="trainer-ilt-location-title">
                    <h3 className="trainer-ilt-location-name">{room.name}</h3>
                    <p className="trainer-ilt-location-city">Room {room.roomNumber}</p>
                  </div>
                  <span className={`trainer-ilt-status trainer-ilt-status-${room.status.toLowerCase()}`}>
                    {room.status}
                  </span>
                </div>

                <div className="trainer-ilt-location-details">
                  <div className="trainer-ilt-detail-item">
                    <FaBuilding /> {room.location}
                  </div>
                  <div className="trainer-ilt-detail-item">
                    <FaMapMarkerAlt /> {room.floor}
                  </div>
                </div>

                <div className="trainer-ilt-location-info">
                  <div className="trainer-ilt-info-group">
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Type</div>
                      <div className="trainer-ilt-info-value">{room.type}</div>
                    </div>
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Capacity</div>
                      <div className="trainer-ilt-info-value">{room.capacity}</div>
                    </div>
                  </div>
                  <div className="trainer-ilt-info-group">
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Area</div>
                      <div className="trainer-ilt-info-value">{room.area}</div>
                    </div>
                  </div>
                </div>

                <div className="trainer-ilt-facilities">
                  <div className="trainer-ilt-facilities-label">Amenities</div>
                  <div className="trainer-ilt-facilities-list">
                    {room.amenities.map((amenity, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-location-actions">
                  <button className="trainer-ilt-btn-view">View Details</button>
                  <button className="trainer-ilt-icon-btn">
                    <FaEdit />
                  </button>
                  <button className="trainer-ilt-icon-btn">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'trainers' && (
          <div className="trainer-ilt-locations-grid">
            {filteredTrainers.map(trainer => (
              <div key={trainer.id} className="trainer-ilt-location-card">
                <div className="trainer-ilt-location-header">
                  <div className="trainer-ilt-location-icon" style={{ backgroundColor: trainer.color, fontSize: '18px', fontWeight: '600' }}>
                    {trainer.initials}
                  </div>
                  <div className="trainer-ilt-location-title">
                    <h3 className="trainer-ilt-location-name">{trainer.firstName} {trainer.lastName}</h3>
                    <p className="trainer-ilt-location-city">{trainer.email}</p>
                  </div>
                  <span className={`trainer-ilt-status trainer-ilt-status-${trainer.status.toLowerCase().replace(' ', '')}`}>
                    {trainer.status}
                  </span>
                </div>

                <div className="trainer-ilt-detail-item" style={{ marginBottom: '12px' }}>
                  <FaStar style={{ color: '#FFA000' }} /> {trainer.rating} · {trainer.sessions} sessions
                </div>

                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px', lineHeight: '1.5' }}>
                  {trainer.bio}
                </p>

                <div className="trainer-ilt-facilities" style={{ marginBottom: '16px' }}>
                  <div className="trainer-ilt-facilities-label">Expertise</div>
                  <div className="trainer-ilt-facilities-list">
                    {trainer.expertise.map((skill, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag" style={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-facilities" style={{ marginBottom: '16px' }}>
                  <div className="trainer-ilt-facilities-label">Certifications</div>
                  <div className="trainer-ilt-facilities-list">
                    {trainer.certifications.map((cert, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-location-info">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Experience</div>
                    <div className="trainer-ilt-info-value">{trainer.experience} years</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Rate</div>
                    <div className="trainer-ilt-info-value">${trainer.rate}/hr</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Availability</div>
                    <div className="trainer-ilt-info-value">{trainer.availability}</div>
                  </div>
                </div>

                <div className="trainer-ilt-detail-item" style={{ marginTop: '12px' }}>
                  <FaGlobeAmericas /> {trainer.languages.join(', ')}
                </div>

                <div className="trainer-ilt-location-actions">
                  <button className="trainer-ilt-btn-view">View Profile</button>
                  <button className="trainer-ilt-icon-btn">
                    <FaEdit />
                  </button>
                  <button className="trainer-ilt-icon-btn">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="trainer-ilt-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="trainer-ilt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-ilt-modal-header">
              <h3 className="trainer-ilt-modal-title">{activeTab === 'rooms' ? 'Add New Room' : activeTab === 'trainers' ? 'Add New Trainer' : 'Add New Location'}</h3>
              <button
                className="trainer-ilt-modal-close"
                onClick={() => setShowModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            {activeTab === 'locations' && (
              <div className="trainer-ilt-form">
              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Location Name *</label>
                <input 
                  type="text" 
                  name="name"
                  placeholder="e.g., New York Training Center"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="trainer-ilt-input"
                />
              </div>

              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Address *</label>
                <input 
                  type="text" 
                  name="address"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="trainer-ilt-input"
                />
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">City *</label>
                  <input 
                    type="text" 
                    name="city"
                    placeholder="New York"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">State *</label>
                  <input 
                    type="text" 
                    name="state"
                    placeholder="NY"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Zip Code</label>
                  <input 
                    type="text" 
                    name="zipCode"
                    placeholder="10001"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Country</label>
                  <input 
                    type="text" 
                    name="country"
                    placeholder="USA"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Timezone</label>
                  <select 
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="trainer-ilt-select"
                  >
                    <option value="">IST</option>
                    <option value="EST">EST</option>
                    <option value="CST">CST</option>
                    <option value="MST">MST</option>
                    <option value="PST">PST</option>
                  </select>
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Total Capacity</label>
                  <input 
                    type="number" 
                    name="capacity"
                    placeholder="100"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="location@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Facilities</label>
                <div className="trainer-ilt-facilities-grid">
                  {facilityOptions.map(facility => (
                    <label key={facility.id} className="trainer-ilt-checkbox-label">
                      <input 
                        type="checkbox"
                        checked={formData.facilities.includes(facility.label)}
                        onChange={() => handleFacilityToggle(facility.label)}
                        className="trainer-ilt-checkbox"
                      />
                      <span>{facility.icon} {facility.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="trainer-ilt-modal-actions">
                <button
                  className="trainer-ilt-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="trainer-ilt-btn-primary"
                  onClick={handleSubmit}
                >
                  Create Location
                </button>
              </div>
              </div>
            )}

            {activeTab === 'rooms' && (
              <div className="trainer-ilt-form">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Room Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g., Executive Conference Room"
                    value={roomFormData.name}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                  />
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Room Number</label>
                    <input
                      type="text"
                      name="roomNumber"
                      placeholder="401"
                      value={roomFormData.roomNumber}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Floor</label>
                    <input
                      type="text"
                      name="floor"
                      placeholder="4th Floor"
                      value={roomFormData.floor}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Location *</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="New York Training Center"
                    value={roomFormData.location}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                  />
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Room Type</label>
                    <select
                      name="type"
                      value={roomFormData.type}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-select"
                    >
                      <option value="Conference">Class Room</option>
                      <option value="Conference">Conference</option>
                      <option value="Lab">Lab</option>
                      <option value="Classroom">Classroom</option>
                      <option value="Virtual">Virtual</option>
                    </select>
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Capacity *</label>
                    <input
                      type="number"
                      name="capacity"
                      placeholder="30"
                      value={roomFormData.capacity}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Area (sq ft)</label>
                  <input
                    type="text"
                    name="area"
                    placeholder="1200 sq ft"
                    value={roomFormData.area}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Description</label>
                  <textarea
                    name="description"
                    placeholder="Describe the room features and setup..."
                    value={roomFormData.description}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Amenities</label>
                  <div className="trainer-ilt-facilities-grid">
                    {amenityOptions.map(amenity => (
                      <label key={amenity} className="trainer-ilt-checkbox-label">
                        <input
                          type="checkbox"
                          checked={roomFormData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          className="trainer-ilt-checkbox"
                        />
                        <span>{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-modal-actions">
                  <button
                    className="trainer-ilt-btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="trainer-ilt-btn-primary"
                    onClick={handleRoomSubmit}
                  >
                    <FaDoorOpen style={{ marginRight: '8px' }} />
                    Create Room
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'trainers' && (
              <div className="trainer-ilt-form">
                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="John"
                      value={trainerFormData.firstName}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Smith"
                      value={trainerFormData.lastName}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john.smith@company.com"
                      value={trainerFormData.email}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 123-4567"
                      value={trainerFormData.phone}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Bio</label>
                  <textarea
                    name="bio"
                    placeholder="Brief professional biography..."
                    value={trainerFormData.bio}
                    onChange={handleTrainerInputChange}
                    className="trainer-ilt-input"
                    rows={3}
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Expertise Areas</label>
                  <div className="trainer-ilt-facilities-grid">
                    {expertiseOptions.map(expertise => (
                      <label key={expertise} className="trainer-ilt-checkbox-label">
                        <input
                          type="checkbox"
                          checked={trainerFormData.expertise.includes(expertise)}
                          onChange={() => handleExpertiseToggle(expertise)}
                          className="trainer-ilt-checkbox"
                        />
                        <span>{expertise}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Certifications</label>
                  <input
                    type="text"
                    placeholder="Add certification..."
                    className="trainer-ilt-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        const value = e.currentTarget.value.trim();
                        setTrainerFormData(prev => ({
                          ...prev,
                          certifications: [...prev.certifications, value]
                        }));
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  {trainerFormData.certifications.length > 0 && (
                    <div className="trainer-ilt-facilities-list" style={{ marginTop: '8px' }}>
                      {trainerFormData.certifications.map((cert, idx) => (
                        <span key={idx} className="trainer-ilt-facility-tag">
                          {cert}
                          <button
                            type="button"
                            onClick={() => {
                              setTrainerFormData(prev => ({
                                ...prev,
                                certifications: prev.certifications.filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{ marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#E65100' }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-checkbox-label" style={{ padding: '12px', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      name="isExternal"
                      checked={trainerFormData.isExternal}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-checkbox"
                    />
                    <span>External Trainer?</span>
                  </label>
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Experience (years)</label>
                    <input
                      type="number"
                      name="experience"
                      placeholder="0"
                      value={trainerFormData.experience}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Hourly Rate ($)</label>
                    <input
                      type="number"
                      name="rate"
                      placeholder="0"
                      value={trainerFormData.rate}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Availability</label>
                  <select
                    name="availability"
                    value={trainerFormData.availability}
                    onChange={handleTrainerInputChange}
                    className="trainer-ilt-select"
                  >
                    <option value="Full-Time">Full Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Contract">Contract</option>
                  </select>
                </div>

                <div className="trainer-ilt-modal-actions">
                  <button
                    className="trainer-ilt-btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="trainer-ilt-btn-primary"
                    onClick={handleTrainerSubmit}
                  >
                    <FaUserTie style={{ marginRight: '8px' }} />
                    Add Trainer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ILTManagement;